import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { cn } from '@lombok/ui';
import { Field } from './fields';

/**
 * Controlled chips input for string-array fields (e.g. territoryIds,
 * allowedLocationIds). Designed for RHF's <Controller>: pass `value` (string[])
 * and `onChange`. Add a chip with Enter or comma; remove with the × or
 * Backspace on an empty input. Tokens only — no hardcoded colors.
 */
export interface TagsFieldProps {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  hint?: string;
  error?: string;
  inputId?: string;
}

export function TagsField({
  label,
  value,
  onChange,
  placeholder = 'Escribe y presiona Enter…',
  hint,
  error,
  inputId,
}: TagsFieldProps) {
  const [draft, setDraft] = useState('');

  const commit = (raw: string) => {
    const next = raw.trim();
    if (!next) return;
    if (!value.includes(next)) onChange([...value, next]);
    setDraft('');
  };

  const remove = (tag: string) => onChange(value.filter((t) => t !== tag));

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      remove(value[value.length - 1]!);
    }
  };

  return (
    <Field label={label} htmlFor={inputId} error={error} hint={hint}>
      <div
        className={cn(
          'flex min-h-11 flex-wrap items-center gap-1.5 rounded-md border bg-surface px-2 py-1.5',
          'focus-within:border-brand-accent focus-within:ring-2 focus-within:ring-ring',
          error ? 'border-danger' : 'border-border-strong',
        )}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-brand-primary"
          >
            {tag}
            <button
              type="button"
              onClick={() => remove(tag)}
              className="text-brand-primary/70 hover:text-brand-primary"
              aria-label={`Quitar ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={inputId}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => commit(draft)}
          placeholder={value.length === 0 ? placeholder : ''}
          className="h-7 min-w-[8rem] flex-1 bg-transparent px-1 text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
        />
      </div>
    </Field>
  );
}
